import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { OutreachTargetKind } from 'fym-shared/src/music24/outreach/types';
import { TypedMemo } from '../../../common/types';
import { FitRing } from '../components/FitRing';
import { TargetCover } from '../find/TargetCover';
import { TARGET_KIND_LABEL } from '../outreachLabels';
import { serviceBrand } from '../outreachServiceBrand';
import { compactCount } from '../outreachFormat';
import type { OutreachLiveMatch } from '../outreachTypes';
import { liveMessages as messages } from './liveMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the live screens; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Live.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The kind of a target that the cover can draw; an unknown kind (a newer server) draws a playlist. */
export function knownKind(kind: string | null): OutreachTargetKind {
  return kind !== null && kind in TARGET_KIND_LABEL ? (kind as OutreachTargetKind) : 'playlist';
}

/** One real match of the search (design t1): cover, name, kind · followers · why, email, fit. */
const SearchMatchRowComponent: React.FC<{ match: OutreachLiveMatch; isNew: boolean }> = ({ match, isNew }) => {
  const intl = useIntl();
  const kind = knownKind(match.kind);
  const parts = [
    match.service === null ? null : serviceBrand(match.service).name,
    intl.formatMessage(TARGET_KIND_LABEL[kind]),
    match.followers === null
      ? null
      : intl.formatMessage(messages.followers, { count: compactCount(intl, match.followers) }),
    match.why,
  ].filter((part): part is string => part !== null && part !== '');
  return (
    <li className={clsx(styles.match, isNew && styles.matchNew)} data-testid="search-match">
      <TargetCover imageUrl={match.coverUrl} kind={kind} className={shared.cover} />
      <div className={styles.matchText}>
        <div className={shared.row}>
          <span className={styles.matchName}>{match.name}</span>
          {isNew && <span className={clsx(shared.chip, shared.chipVio)}>{intl.formatMessage(messages.justFound)}</span>}
        </div>
        <div className={styles.matchSub}>{parts.join(' · ')}</div>
      </div>
      {match.hasEmail && (
        <span className={clsx(shared.chip, shared.chipPos, 'd-none d-md-inline-flex')}>
          {intl.formatMessage(messages.emailFound)}
        </span>
      )}
      <FitRing score={match.fit} />
    </li>
  );
};

export const SearchMatchRow = TypedMemo(SearchMatchRowComponent);
